import { FC, Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { Route, Link, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useVersion } from 'mir-utils/api-tools';
import { Box } from '@mui/material';
import { Version } from '__generated__/version';
import { Loading } from './Loading';

export type TabDescription = {
  component: () => Promise<any>;
  label: string;
  name: string;
  pathName: string;
  hide?: (v: Version) => boolean;
};

interface TabsMuiProps {
  children: TabDescription[];
}

const CONTAINER_STYLES = {
  display: 'flex',
  height: '100%',
  minHeight: '100%',
  flexDirection: 'column',
};

/**
 * @summary Кастомизированный компонент Tabs от material-ui.
 *
 * @param children - Список табов для отображения.
 */
export const TabsMUI: FC<TabsMuiProps> = ({ children }) => {
  const { path, url, isExact } = useRouteMatch();
  const { pathname } = useLocation();
  const version = useVersion();
  const componentIndex = children.findIndex(
    (element) => element.pathName.length > 0 && pathname.includes(element.pathName)
  );
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(
    componentIndex === -1 ? 0 : componentIndex
  );

  // При смене компонента, не всегда меняется индекс таба. Например при нажатии на модуль в меню.
  // Этот useEffect правит это.
  useEffect(() => {
    if (isExact) {
      setCurrentTabIndex(0);
    }
  }, [isExact]);

  const routes = useMemo(
    () =>
      children.map((tab) => {
        const Component = lazy(tab.component);
        return (
          <Route path={`${path}${tab.pathName}`} key={tab.name} exact={tab.pathName === ''}>
            <Box sx={CONTAINER_STYLES}>
              <Component />
            </Box>
          </Route>
        );
      }),
    [children, currentTabIndex]
  );

  const zeroTab = useMemo(() => children.find((tab) => tab.pathName === ''), [children]);
  const ZeroComponent = useMemo(() => zeroTab && lazy(zeroTab.component), [zeroTab]);

  if (version === undefined) {
    return <Loading />;
  }

  return (
    <Box sx={CONTAINER_STYLES}>
      <Box mb={2}>
        <Tabs
          textColor="inherit"
          indicatorColor="secondary"
          value={currentTabIndex}
          onChange={(_event, item) => setCurrentTabIndex(item)}
          sx={{
            // иначе почему-то табы режутся по ширине контента внутри таба
            minWidth: 'max-content',
          }}
        >
          {children.map((tab, index) => {
            if (tab.hide !== undefined && tab.hide(version)) {
              return null;
            }
            return (
              <Tab
                label={tab.label}
                sx={{
                  textTransform: 'none',
                  letterSpacing: 'unset',
                }}
                key={`tab-${index}`}
                component={Link}
                to={`${url}${tab.pathName}`}
                value={index}
              />
            );
          })}
        </Tabs>
      </Box>
      <Box flexGrow={1}>
        <Suspense fallback={<Loading />}>
          <Switch>
            {routes}
            {zeroTab && (
              // Когда ни один из routes не подходит, надо рендерить с нулевым путем
              <Route>
                <Box sx={CONTAINER_STYLES}>{ZeroComponent}</Box>
              </Route>
            )}
          </Switch>
        </Suspense>
      </Box>
    </Box>
  );
};
